import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  PlayArrow, 
  Tv, 
  Link as LinkIcon,
  Info,
  PhoneAndroid,
  ConnectedTv,
  Language as WebIcon
} from '@mui/icons-material';

const StreamSettingsTab = ({ formData, onInputChange }) => {
  const getStreamType = (url) => {
    if (!url) return null;
    if (url.includes('.m3u8')) return 'HLS';
    if (url.includes('.mpd')) return 'DASH';
    if (url.includes('rtmp://')) return 'RTMP';
    if (url.includes('.mp4')) return 'MP4';
    return 'Другой';
  };

  const streamType = getStreamType(formData.stream);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Настройки потокового вещания
      </Typography>

      <Grid container spacing={3}>
        {/* Основные настройки потока */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlayArrow color="primary" />
                Основной поток
              </Typography>
              
              <TextField
                fullWidth
                label="Stream URL"
                value={formData.stream}
                onChange={(e) => onInputChange('stream', e.target.value)}
                multiline
                rows={3}
                helperText="Прямая ссылка на видеопоток (HLS, DASH, RTMP, MP4)"
                placeholder="https://example.com/stream.m3u8"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon />
                    </InputAdornment>
                  ),
                  endAdornment: streamType && (
                    <InputAdornment position="end">
                      <Chip label={streamType} size="small" color="primary" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Stream Redirect"
                value={formData.stream_redirect}
                onChange={(e) => onInputChange('stream_redirect', e.target.value)}
                helperText="Альтернативная ссылка для перенаправления (опционально)"
                placeholder="https://example.com/redirect.m3u8"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Vitrina Config URL"
                value={formData.vitrina_config_url || ''}
                onChange={(e) => onInputChange('vitrina_config_url', e.target.value || null)}
                helperText="URL конфига витрины для данного канала (опционально)"
                placeholder="https://example.com/vitrina/config.json"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon />
                    </InputAdornment>
                  )
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Настройки фрейма */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tv color="primary" />
                Встроенный плеер
              </Typography>
              
              <TextField
                fullWidth
                label="Frame URL"
                value={formData.frame}
                onChange={(e) => onInputChange('frame', e.target.value)}
                multiline
                rows={3}
                helperText="Ссылка на встроенный плеер или iframe (опционально)"
                placeholder="https://example.com/player.html"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Настройки типов плееров */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlayArrow color="primary" />
                Типы плееров для устройств
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Тип плеера для мобильных</InputLabel>
                    <Select
                      value={formData.player_type_mobile || 'frame'}
                      label="Тип плеера для мобильных"
                      onChange={(e) => onInputChange('player_type_mobile', e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <PhoneAndroid color="action" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="frame">Frame (встроенный плеер)</MenuItem>
                      <MenuItem value="stream">Stream (прямой поток)</MenuItem>
                      <MenuItem value="redirect">Redirect (перенаправление)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Тип плеера для ТВ</InputLabel>
                    <Select
                      value={formData.player_type_tv || 'stream'}
                      label="Тип плеера для ТВ"
                      onChange={(e) => onInputChange('player_type_tv', e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <ConnectedTv color="action" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="stream">Stream (прямой поток)</MenuItem>
                      <MenuItem value="frame">Frame (встроенный плеер)</MenuItem>
                      <MenuItem value="redirect">Redirect (перенаправление)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Тип плеера для WEB</InputLabel>
                    <Select
                      value={formData.player_type_web || 'frame'}
                      label="Тип плеера для WEB"
                      onChange={(e) => onInputChange('player_type_web', e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <WebIcon color="action" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="frame">Frame (встроенный плеер)</MenuItem>
                      <MenuItem value="stream">Stream (прямой поток)</MenuItem>
                      <MenuItem value="redirect">Redirect (перенаправление)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Frame:</strong> Использует встроенный плеер (iframe)<br/>
                  <strong>Stream:</strong> Прямая ссылка на поток<br/>
                  <strong>Redirect:</strong> Перенаправление на внешний плеер
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Информация о потоке */}
        <Grid item xs={12}>
          <Alert severity="info" icon={<Info />}>
            <Typography variant="subtitle2" gutterBottom>
              Типы поддерживаемых потоков:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip label="HLS (.m3u8)" size="small" variant="outlined" />
              <Chip label="DASH (.mpd)" size="small" variant="outlined" />
              <Chip label="RTMP" size="small" variant="outlined" />
              <Chip label="MP4" size="small" variant="outlined" />
            </Box>
          </Alert>
        </Grid>

        {/* Предварительный просмотр */}
        {formData.stream && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Предварительный просмотр настроек
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Тип потока:
                    </Typography>
                    <Chip label={streamType || 'Неизвестно'} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Основной поток:
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {formData.stream}
                    </Typography>
                  </Box>
                  {formData.stream_redirect && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Перенаправление:
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {formData.stream_redirect}
                      </Typography>
                    </Box>
                  )}
                  {formData.frame && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Встроенный плеер:
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {formData.frame}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default StreamSettingsTab;
